import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Calendar, CreditCard, MapPin, ArrowRight, Star, Award, Shield } from 'lucide-react';
import BookingSearch from '../components/BookingSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { t, i18n } = useTranslation();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${API}/properties`);
        const data = Array.isArray(response.data) ? response.data : [];
        setFeaturedProperties(data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  return (
    <div className="bg-stone-50">
      <Helmet>
        <title>{i18n.language === 'el' ? 'InFox Suites — Καταλύματα σε Θεσσαλονίκη & Χαλκιδική' : 'InFox Suites — Vacation Rentals in Thessaloniki & Halkidiki'}</title>
        <meta name="description" content={i18n.language === 'el' ? 'Κρατήστε απευθείας πολυτελή καταλύματα στη Θεσσαλονίκη και τη Χαλκιδική. Καλύτερη τιμή, χωρίς χρεώσεις πλατφόρμας.' : 'Book luxury apartments directly in Thessaloniki and Halkidiki. Best price guaranteed, no platform fees.'} />
        <meta property="og:title" content="InFox Suites — Vacation Rentals in Thessaloniki & Halkidiki" />
        <meta property="og:description" content="Book luxury apartments directly in Thessaloniki and Halkidiki. Best price guaranteed." />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Premium Hero Section */}
      <section
        className="relative min-h-[85vh] flex items-center justify-center"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.55)), url(https://images.unsplash.com/photo-1766261010715-f5c230be72f7?w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
          <div className="text-center mb-12">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_vacation-booking-11/artifacts/2nn2marf_InfoXSuites-logo-orizontio.png"
                alt="InFox Suites"
                className="h-16 md:h-20 w-auto opacity-95"
              />
            </div>
            
            {/* Headline */}
            <h1 className="text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight leading-tight">
              {i18n.language === 'el' 
                ? 'Πολυτελείς Διαμονές στη Θεσσαλονίκη και τη Χαλκιδική'
                : 'Premium Stays in Thessaloniki and Halkidiki'}
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl font-light text-white/90 mb-12 max-w-2xl mx-auto leading-relaxed">
              {i18n.language === 'el'
                ? 'Επιλέξτε τις ημερομηνίες σας και ανακαλύψτε το ιδανικό κατάλυμα'
                : 'Select your dates and discover your perfect accommodation'}
            </p>
          </div>

          {/* Booking Search Module */}
          <BookingSearch />
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {/* Booking.com Score */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <Star className="w-5 h-5 fill-primary text-primary" />
                <Star className="w-5 h-5 fill-primary text-primary" />
                <Star className="w-5 h-5 fill-primary text-primary" />
                <Star className="w-5 h-5 fill-primary text-primary" />
                <Star className="w-5 h-5 fill-primary text-primary" />
              </div>
              <div className="border-l border-stone-200 pl-3">
                <div className="text-2xl font-semibold text-primary">9.2</div>
                <div className="text-xs text-stone-500">Booking.com</div>
              </div>
            </div>

            {/* Airbnb Superhost */}
            <div className="flex items-center space-x-3 px-6 py-3 bg-stone-50 rounded-sm border border-stone-200">
              <Award className="w-6 h-6 text-primary" />
              <div>
                <div className="font-semibold text-stone-900">Airbnb Superhost</div>
                <div className="text-xs text-stone-500">
                  {i18n.language === 'el' ? 'Πιστοποιημένη Ποιότητα' : 'Certified Quality'}
                </div>
              </div>
            </div>

            {/* Fully Insured */}
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-primary" />
              <div>
                <div className="font-semibold text-stone-900">
                  {i18n.language === 'el' ? 'Πλήρως Ασφαλισμένα' : 'Fully Insured'}
                </div>
                <div className="text-xs text-stone-500">
                  {i18n.language === 'el' ? 'Όλα τα καταλύματα' : 'All properties'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-semibold text-center mb-16">
            {t('home.features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('home.features.availability.title')}</h3>
              <p className="text-stone-600 font-light leading-relaxed">
                {t('home.features.availability.desc')}
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-6">
                <CreditCard className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('home.features.payment.title')}</h3>
              <p className="text-stone-600 font-light leading-relaxed">
                {t('home.features.payment.desc')}
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('home.features.location.title')}</h3>
              <p className="text-stone-600 font-light leading-relaxed">
                {t('home.features.location.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold mb-4">{t('home.featured.title')}</h2>
            <p className="text-lg text-stone-600 font-light">{t('home.featured.subtitle')}</p>
            <div className="mt-6">
              <Link
                to="/properties"
                className="inline-flex items-center space-x-2 border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-sm transition-colors"
                data-testid="explore-properties-button"
              >
                <span className="font-medium">{t('home.hero.cta')}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-stone-500">{t('home.loading')}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProperties.map((property) => (
                <Link
                  key={property.id}
                  to={`/properties/${property.slug}`}
                  className="group bg-white rounded-sm border border-stone-200 overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  data-testid={`property-card-${property.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                    <img
                      src={property.images[0]}
                      alt={property.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {property.name}
                    </h3>
                    <p className="text-sm text-stone-600 font-light mb-4 flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {property.location}
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-semibold text-primary">
                          €{property.base_price_per_night}
                        </span>
                        <span className="text-sm text-stone-500 font-light ml-2">
                          {t('property.perNight')}
                        </span>
                      </div>
                      <div className="text-sm text-stone-600">
                        {property.max_guests} {t('property.guests')}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/properties"
              className="inline-flex items-center space-x-2 border border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-sm transition-colors"
              data-testid="view-all-properties"
            >
              <span className="font-medium">{t('home.featured.viewAll')}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-lg font-light mb-8 text-white/90 leading-relaxed">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/properties"
            className="inline-block bg-white text-primary hover:bg-stone-100 px-8 py-4 rounded-sm transition-colors font-semibold text-sm uppercase tracking-wide"
            data-testid="book-now-cta"
          >
            {t('home.cta.button')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
